import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { importLibrary } from '@googlemaps/js-api-loader';
import {
  computeBoundsSpanKm,
  computeFootprintHeatRadii,
  pickInitialFootprintFocus,
} from '../../lib/employerFootprintMapLayout';
import {
  configureGoogleMapsLoader,
  googleMapsApiKey,
  googleMapsMapId,
} from '../../lib/googleMapsRuntime';

const PURCHASE_COLOR = '#ed6c02';
const EMPLOYER_COLOR = '#1565c0';

function employerPinEl(): HTMLElement {
  const el = document.createElement('div');
  el.style.width = '22px';
  el.style.height = '22px';
  el.style.borderRadius = '50%';
  el.style.border = '3px solid #fff';
  el.style.boxShadow = '0 2px 8px rgba(21,101,192,0.55)';
  el.style.background = EMPLOYER_COLOR;
  el.style.display = 'grid';
  el.style.placeItems = 'center';
  el.style.cursor = 'pointer';
  const inner = document.createElement('div');
  inner.style.width = '6px';
  inner.style.height = '6px';
  inner.style.borderRadius = '50%';
  inner.style.background = '#fff';
  el.appendChild(inner);
  return el;
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function bearingDeg(from: { lat: number; lng: number }, to: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const φ1 = toRad(from.lat);
  const φ2 = toRad(to.lat);
  const Δλ = toRad(to.lng - from.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function compassLabel(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
  return dirs[Math.round(deg / 45) % 8]!;
}

type Props = {
  purchases: Array<{
    merchantName: string;
    address: string | null;
    latitude: number;
    longitude: number;
    category: string | null;
  }>;
  employer: {
    name: string;
    address: string | null;
    latitude: number;
    longitude: number;
    distanceKm: number | null;
  } | null;
  height?: number | string;
  /** When false the map stays mounted but hidden — triggers resize on re-activation. */
  active?: boolean;
};

type FocusTarget = 'all' | 'purchases' | 'employer';

export function EmployerFootprintMap({
  purchases,
  employer,
  height = 320,
  active = true,
}: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObjRef = useRef<google.maps.Map | null>(null);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'missing_key' | 'error' | 'empty'>(
    'idle',
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [employerOffScreen, setEmployerOffScreen] = useState(false);
  const [employerBearing, setEmployerBearing] = useState<number | null>(null);

  const purchasePts = useMemo(
    () => purchases.map((p) => ({ lat: p.latitude, lng: p.longitude })),
    [purchases],
  );
  const employerPt = useMemo(
    () => (employer ? { lat: employer.latitude, lng: employer.longitude } : null),
    [employer],
  );
  const purchaseCentroid = useMemo(() => {
    if (purchasePts.length === 0) return null;
    return {
      lat: purchasePts.reduce((s, p) => s + p.lat, 0) / purchasePts.length,
      lng: purchasePts.reduce((s, p) => s + p.lng, 0) / purchasePts.length,
    };
  }, [purchasePts]);

  const initialFocus = useMemo(
    () => pickInitialFootprintFocus(purchasePts, employerPt),
    [purchasePts, employerPt],
  );

  const pinKey = useMemo(
    () =>
      JSON.stringify({
        purchases: purchasePts,
        employer: employerPt,
      }),
    [purchasePts, employerPt],
  );

  const fitTo = useCallback(
    (target: FocusTarget) => {
      const map = mapObjRef.current;
      if (!map || typeof google === 'undefined') return;

      const bounds = new google.maps.LatLngBounds();
      let count = 0;
      if (target === 'all' || target === 'purchases') {
        for (const p of purchasePts) {
          bounds.extend(p);
          count += 1;
        }
      }
      if ((target === 'all' || target === 'employer') && employerPt) {
        bounds.extend(employerPt);
        count += 1;
      }
      if (count === 0) return;

      if (count === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(target === 'employer' ? 12 : 11);
      } else {
        map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
      }

      if (target === 'employer' && employer) {
        setHint(
          employer.address
            ? `Showing possible employer: ${employer.name} — ${employer.address}`
            : `Showing possible employer: ${employer.name}`,
        );
      } else if (target === 'purchases') {
        setHint(
          purchasePts.length === 1
            ? 'Showing bank purchase area'
            : `Showing ${purchasePts.length} bank purchase locations (heat)`,
        );
      } else {
        setHint(null);
      }
    },
    [employer, employerPt, purchasePts],
  );

  const updateOffScreen = useCallback(() => {
    const map = mapObjRef.current;
    if (!map || !employerPt) {
      setEmployerOffScreen(false);
      setEmployerBearing(null);
      return;
    }
    const bounds = map.getBounds();
    const center = map.getCenter();
    if (!bounds || !center) return;
    const visible = bounds.contains(employerPt);
    setEmployerOffScreen(!visible);
    if (!visible) {
      setEmployerBearing(bearingDeg({ lat: center.lat(), lng: center.lng() }, employerPt));
    } else {
      setEmployerBearing(null);
    }
  }, [employerPt]);

  const updateOffScreenRef = useRef(updateOffScreen);
  updateOffScreenRef.current = updateOffScreen;
  const fitToRef = useRef(fitTo);
  fitToRef.current = fitTo;

  useEffect(() => {
    if (!active || phase !== 'ready') return;
    const map = mapObjRef.current;
    if (!map || typeof google === 'undefined') return;

    let frame = 0;
    const syncLayout = () => {
      google.maps.event.trigger(map, 'resize');
      fitToRef.current(initialFocus);
      updateOffScreenRef.current();
    };
    frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(syncLayout);
    });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [active, initialFocus, phase]);

  useEffect(() => {
    if (phase !== 'ready' || !mapRef.current) return;
    const map = mapObjRef.current;
    if (!map || typeof google === 'undefined') return;

    const observer = new ResizeObserver(() => {
      google.maps.event.trigger(map, 'resize');
      updateOffScreenRef.current();
    });
    observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, [phase]);

  useEffect(() => {
    if (purchasePts.length === 0 && !employerPt) {
      setPhase('empty');
      return;
    }

    if (!googleMapsApiKey()) {
      setPhase('missing_key');
      return;
    }

    let cancelled = false;
    const heatCircles: google.maps.Circle[] = [];
    let employerMarker: google.maps.marker.AdvancedMarkerElement | null = null;
    let linkLine: google.maps.Polyline | null = null;
    let idleListener: google.maps.MapsEventListener | null = null;
    setPhase('loading');
    setLoadError(null);

    void (async () => {
      try {
        const mapId = googleMapsMapId();
        configureGoogleMapsLoader();

        // LatLngBounds / event / SymbolPath live on `core`, not `maps`.
        const { LatLngBounds, event, SymbolPath, ControlPosition } = await importLibrary('core');
        const { Map, Polyline, Circle } = await importLibrary('maps');
        const { AdvancedMarkerElement } = await importLibrary('marker');

        if (cancelled || !mapRef.current) return;

        const bounds = new LatLngBounds();
        for (const p of purchasePts) bounds.extend(p);
        if (employerPt) bounds.extend(employerPt);

        const map = new Map(mapRef.current, {
          center: bounds.getCenter(),
          zoom: 9,
          mapTypeControl: false,
          fullscreenControl: true,
          streetViewControl: true,
          streetViewControlOptions: { position: ControlPosition.RIGHT_BOTTOM },
          fullscreenControlOptions: { position: ControlPosition.RIGHT_BOTTOM },
          gestureHandling: 'greedy',
          scrollwheel: true,
          mapId,
        });
        mapObjRef.current = map;

        // Scale heat blobs to the full pin span so purchases stay visible when
        // purchase cluster and employer are far apart (Bug_100).
        const allPts = employerPt ? [...purchasePts, employerPt] : purchasePts;
        const spanKm = computeBoundsSpanKm(allPts);
        const { outerR, midR, coreR, fillOpacity: heatFillOpacity } =
          computeFootprintHeatRadii(spanKm);

        // Soft heat blobs (works with vector maps / mapId).
        for (const p of purchasePts) {
          heatCircles.push(
            new Circle({
              map,
              center: p,
              radius: outerR,
              fillColor: PURCHASE_COLOR,
              fillOpacity: heatFillOpacity,
              strokeWeight: 0,
              clickable: false,
            }),
            new Circle({
              map,
              center: p,
              radius: midR,
              fillColor: PURCHASE_COLOR,
              fillOpacity: 0.4,
              strokeWeight: 0,
              clickable: false,
            }),
            new Circle({
              map,
              center: p,
              radius: coreR,
              fillColor: PURCHASE_COLOR,
              fillOpacity: 0.85,
              strokeColor: '#fff',
              strokeOpacity: 0.9,
              strokeWeight: 1.5,
              clickable: false,
            }),
          );
        }

        if (employerPt && employer) {
          employerMarker = new AdvancedMarkerElement({
            map,
            position: employerPt,
            title: employer.address
              ? `${employer.name} — ${employer.address}`
              : employer.name,
            content: employerPinEl(),
          });
        }

        if (purchaseCentroid && employerPt) {
          linkLine = new Polyline({
            path: [purchaseCentroid, employerPt],
            geodesic: true,
            strokeColor: EMPLOYER_COLOR,
            strokeOpacity: 0,
            strokeWeight: 3,
            icons: [
              {
                icon: {
                  path: 'M 0,-1 0,1',
                  strokeOpacity: 0.85,
                  strokeColor: EMPLOYER_COLOR,
                  scale: 3,
                },
                offset: '0',
                repeat: '12px',
              },
              {
                icon: {
                  path: SymbolPath.FORWARD_CLOSED_ARROW,
                  strokeColor: EMPLOYER_COLOR,
                  fillColor: EMPLOYER_COLOR,
                  fillOpacity: 1,
                  scale: 3.5,
                  strokeWeight: 1,
                },
                offset: '100%',
              },
            ],
            map,
          });
        }

        // Distant HQ: open on purchase heat so blobs are visible; employer via legend / off-screen chip.
        if (initialFocus === 'purchases' && purchasePts.length > 0) {
          const purchaseBounds = new LatLngBounds();
          for (const p of purchasePts) purchaseBounds.extend(p);
          map.fitBounds(purchaseBounds, { top: 56, right: 56, bottom: 56, left: 56 });
        } else {
          map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: 56 });
        }
        idleListener = event.addListener(map, 'idle', () => {
          if (!cancelled) updateOffScreenRef.current();
        });

        if (!cancelled) {
          setPhase('ready');
          updateOffScreenRef.current();
          if (purchaseCentroid && employerPt) {
            const km = haversineKm(purchaseCentroid, employerPt);
            const dir = compassLabel(bearingDeg(purchaseCentroid, employerPt));
            setHint(
              `Purchases (heat) → possible employer about ${km.toFixed(0)} km ${dir}. Click the legend to jump.`,
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Unknown map error');
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      for (const c of heatCircles) c.setMap(null);
      linkLine?.setMap(null);
      if (employerMarker) employerMarker.map = null;
      if (idleListener) idleListener.remove();
      if (mapRef.current) mapRef.current.replaceChildren();
      mapObjRef.current = null;
    };
    // pinKey already encodes purchase/employer coordinates; keep the effect narrow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinKey]);

  if (phase === 'empty') {
    return (
      <Typography variant="body2" color="text.secondary">
        No map pins yet — purchase or employer coordinates were not returned.
      </Typography>
    );
  }

  if (phase === 'missing_key') {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> to show purchase and employer locations on the map.
        </Typography>
      </Box>
    );
  }

  if (phase === 'error') {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'error.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography variant="caption" color="error.main" textAlign="center">
          Could not load the footprint map.
          {loadError ? ` (${loadError})` : ''}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          position: 'relative',
          height,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          touchAction: 'none',
        }}
      >
        {phase === 'loading' || phase === 'idle' ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              zIndex: 1,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}
        <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />

        {employerOffScreen && employerBearing != null ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => fitTo('employer')}
            startIcon={<WorkRoundedIcon />}
            sx={{
              position: 'absolute',
              zIndex: 2,
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              boxShadow: 3,
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="span"
                aria-hidden
                sx={{
                  display: 'inline-block',
                  width: 0,
                  height: 0,
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderBottom: '9px solid currentColor',
                  transform: `rotate(${employerBearing}deg)`,
                }}
              />
              Employer {compassLabel(employerBearing)} — show
            </Box>
          </Button>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <ButtonBase
          onClick={() => fitTo('purchases')}
          disabled={purchasePts.length === 0 || phase !== 'ready'}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-disabled': { opacity: 0.45 },
          }}
        >
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: PURCHASE_COLOR }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Bank purchases (heat)
          </Typography>
        </ButtonBase>

        <ButtonBase
          onClick={() => fitTo('employer')}
          disabled={!employerPt || phase !== 'ready'}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-disabled': { opacity: 0.45 },
          }}
        >
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: EMPLOYER_COLOR }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Possible employer
          </Typography>
        </ButtonBase>

        <Button
          size="small"
          variant="text"
          startIcon={<MyLocationRoundedIcon fontSize="small" />}
          disabled={phase !== 'ready'}
          onClick={() => fitTo('all')}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Show both
        </Button>
      </Stack>

      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {hint}
        </Typography>
      ) : null}
    </Stack>
  );
}
